import React, { useState } from "react"
import styled, { css } from "styled-components"
import { media } from "utils/Media"
import ContainerMax from "components/shared/ContainerMax"
import WPLink from "components/shared/WPLink"

const DayCardWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 1rem;
  margin: 0 -1rem;
`

const DayCard = styled.div`
  width: 100%;
  padding: 1rem;
  cursor: pointer;

  @media ${media.lg} {
    width: calc(100% / 7);
  }

  .inner {
    padding: 1rem;
    border: 1px solid #D9D9D9;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);

    @media ${media.lg} {
      padding: .5rem;
    }

    @media ${media.xl} {
      padding: 1rem;
    }

    ${props => props.active && css`
      background: ${props => props.theme.colors.primary};
      color: white;
    `}

    p {
      display: inline-block;

      ${props => props.active && css`
        display: block;
      `}

      @media ${media.lg} {
        display: block;
        padding-right: 0;
      }
    }

    .day {
      text-transform: uppercase;
      font-size: ${props => props.theme.font.h2.size};
      font-family: ${props => props.theme.font.family.bold};
      margin: 0;
      line-height: 1;
      padding-right: .5rem;

      ${props => props.active && css`
        font-size: ${props => props.theme.font.h1.size};
      `}

      @media ${media.lg} {
        font-size: ${props => props.theme.font.h4.size};
        padding-right: 0;
      }

      @media ${media.xl} {
        font-size: ${props => props.theme.font.h3.size};
      }
    }

    .date {
      font-size: ${props => props.theme.font.h3.size};
      font-family: ${props => props.theme.font.family.bold};
      line-height: 1;
      padding-right: 0;
      margin-bottom: .5rem;

      @media ${media.lg} {
        font-size: ${props => props.theme.font.size.sm};
      }

      @media ${media.xl} {
        font-size: ${props => props.theme.font.size.base};
      }
    }

    .preview {
      font-size: ${props => props.theme.font.size.xs};
      font-family: ${props => props.theme.font.family.bold};
    }
  }
`

const DayEventsWrap = styled.div`
  width: 100%;
  display: none;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 0 0 0;

  @media ${media.lg} {
    padding: 2rem;
  }

  ${props => props.active && css`
    display: block;
  `}

  .date {
    padding-bottom: .5rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid white;
    justify-content: space-between;
    display: none;

    @media ${media.lg} {
      display: flex;
    }

    .day {
      font-size: ${props => props.theme.font.h1.size};
      font-family: ${props => props.theme.font.family.bold};
      text-transform: uppercase;
      display: block;
      line-height: 1;

      @media ${media.lg} {
        display: inline-block;
      }
    }

    .eventdate {
      font-size: ${props => props.theme.font.h2.size};
      font-family: ${props => props.theme.font.family.bold};
      display: block;
      padding-bottom: 1rem;
      
      @media ${media.lg} {
        display: inline-block;
        padding-bottom: 0;
      }
    }

    .previewtext {
      font-family: ${props => props.theme.font.family.bold};
    }
  }

  .event {
    padding-bottom: 1rem;

    &.last {
      padding-bottom: 0;
    }

    @media ${media.lg} {
      padding-bottom: 2rem;
    }

    &-title {
      border-bottom: 1px solid ${props => props.theme.colors.secondary};
      padding-bottom: .25rem;
      text-transform: uppercase;
      color: ${props => props.theme.colors.secondary};
      font-family: ${props => props.theme.font.family.bold};
      font-size: ${props => props.theme.font.h2.size};
    }

    &-item {
      padding: .75rem .0;
      display: flex;
      flex-wrap: wrap;
      border-bottom: 1px solid ${props => props.theme.colors.secondary};

      &:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }
    
      @media ${media.lg} {
        flex-wrap: nowrap;
        padding: .75rem .5rem;
      }

      div {
        padding: 0 0 .25rem;
        font-size: ${props => props.theme.font.size.sm};

        @media ${media.lg} {
          padding: 0 .5rem;
          padding: 0 .5rem .25rem;
        }

        @media ${media.xl} {
          font-size: ${props => props.theme.font.size.base};
        }

        &.time {
          width: 50%;
          order: 1;
          @media ${media.lg} {
            width: 20%;
          }
        }

        &.name {
          width: 100%;
          order: 3;
          @media ${media.lg} {
            width: 45%;
            order: 2;
          }
        }

        &.location {
          width: 100%;
          order: 4;
          @media ${media.lg} {
            width: 17.5%;
            order: 3;
          }
        }

        &.link {
          width: 50%;
          order: 2;
          @media ${media.lg} {
            width: 17.5%;
            order: 4;
          }
        }
      }
    }

    a {
      color: white;
      text-decoration: underline;
      font-size: ${props => props.theme.font.size.sm};
    }
  }
`


export default function Schedule({block}) {
  const {
    title,
    days 
  } = block


  const [activeDay, setActiveDay] = useState(0)

  return(
    <ContainerMax 
      maxWidth="1360"
      className="py-4 overflow-hidden"
    >
      {title && (
        <h1 className="text-center pb-3 pb-md-4 m-0 text-uppercase">{title}</h1>
      )}

      <DayCardWrap>
        {days?.map((day, i) => {
          const {
            date,
            previewText
          } = day
          return(
            <DayCard onClick={() => setActiveDay(i)} active={i === activeDay}>
              <div className="inner">
                <p className="day">{getDay(date)}</p>
                <p className="date">{dateFormat(date)}</p>
                {previewText && (
                  <div className="preview" dangerouslySetInnerHTML={{ __html: previewText }} />
                )}
                <div className="d-lg-none">
                  <DayEvent day={day} activeDay={activeDay} id={i} mobile={true} />
                </div>
              </div>
            </DayCard>
          )
        })}
      </DayCardWrap>


      {days?.map((day, i) => {
        return(
          <div className="d-none d-lg-block" key={`eventday${i}`}>
            <DayEvent day={day} activeDay={activeDay} id={i} />
          </div>
        )
      })}

    </ContainerMax>
  )
}

const DayEvent = ({day, activeDay, id, mobile=false}) => {
  const {
    date,
    previewText
  } = day
  return(
    <DayEventsWrap active={id === activeDay} mobile={mobile}>
      <div className="date">
        <div><span className="day">{getDay(date)}</span> <span className="eventdate">{dateFormat(date)}</span></div>

        {previewText && (
          <div dangerouslySetInnerHTML={{ __html: previewText }} className="previewtext" />
        )}
      </div>

      {day.event?.map((event, i) => {
        return(
          <div className={`event ${day.event.length === i+1 ? "last" : ""}`} key={`dayevent${i}${id}`}>
            <div className="event-title">{event.name}</div>
            {event.items?.map((item, i) => {
              return(
                <div className="event-item">
                  <div className="time">
                    <strong>{item.time}</strong>
                  </div>
                  <div className="name">
                    {item.name}
                  </div>
                  <div className="location">
                    {item.location}
                  </div>
                  <div className="text-right link">
                    {item.link && (
                      <WPLink url={item.link.url} target={item.link.url}>
                        {item.link.title}
                      </WPLink>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </DayEventsWrap>
  )
}

const getDay = (dateString) => {
  // Parse the date string into a Date object
  const parts = dateString.split("/");
  const formattedDate = new Date(parts[2], parts[1] - 1, parts[0]);
  const dayOfWeek = formattedDate.getDay();
  // Create an array of day names
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = daysOfWeek[dayOfWeek];

  return dayName
}

const dateFormat = (dateString) => {
  // Parse the date string into a Date object
  const parts = dateString.split("/");
  const formattedDate = new Date(parts[2], parts[1] - 1, parts[0]);

  // Get the day, month, and year
  const day = formattedDate.getDate();
  const monthIndex = formattedDate.getMonth();
  const year = formattedDate.getFullYear();

  const monthNames = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[monthIndex];
  const formattedDateString = day + ' ' + monthName + ' ' + year;

  return formattedDateString;
}